import { Download, FolderMinus, PanelRightClose, PanelRightOpen, RotateCw, SquareActivity } from "lucide-react"
import { useEffect, useState, ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { ActionTooltipButton } from "@/components/buttons/ActionTooltipButton"
import { DownloadAsDocxButton } from "@/components/buttons/DownloadAsDocxButton"
import { DocumentDisplay } from "@/components/displays/DocumentDisplay"
import { EmbeddingsPanel } from "@/components/panels/EmbeddingsPanel"
import { NoEmbeddingsPanel } from "@/components/panels/NoEmbeddingsPanel"
import { NoPropertiesPanel } from "@/components/panels/NoPropertiesPanel"
import { PropertiesPanel } from "@/components/panels/PropertiesPanel"
import { LAST_PANEL_STATE } from "@/constants/cookies"
import { JSON_MAPPINGS } from "@/constants/mappings"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"

import { ContentObjectItem } from "@vertesia/common"

const SCORE_GRANT_PROPOSAL_WORKFLOW_ID = "66c7538f3ec7eb95009bf8d8"

interface DocumentPanelProps {
    children?: ReactNode
    id: string
    removeFromCollection?: (document: string) => void
}
export function DocumentPanel({ id, children, removeFromCollection }: Readonly<DocumentPanelProps>) {
    const { client, project, isLoading } = useUserSession()
    const navigate = useNavigate()

    const [object, setObject] = useState<ContentObjectItem | undefined>(undefined)
    const [review, setReview] = useState<ContentObjectItem | undefined>(undefined)
    const [reviewInProgress, setReviewInProgress] = useState<boolean>(true)
    const [documentUrl, setDocumentUrl] = useState<string | undefined>(undefined)
    const [isPanelOpen, setIsPanelOpen] = useState(localStorage.getItem(LAST_PANEL_STATE) === "open")
    const [panelState, setPanelState] = useState("properties")
    const [mapping, setMapping] = useState<any>(null)

    const navigateTo404 = () => {
        navigate("/404")
    }

    const retrieveMapping = () => {
        const typeId = object?.type?.id
        if (!typeId) {
            return
        }
        setMapping(JSON_MAPPINGS[typeId])
    }

    const searchDocument = () => {
        if (!client || isLoading) {
            return;
        }

        client.objects.retrieve(id).then((response) => {
            if (response) {
                setObject(response)
            } else {
                navigateTo404()
            }
        }).catch(() => {
            navigateTo404()
        })
    }

    const checkReviewExecution = () => {
        if (!client || !object || object.type?.id !== "66c752b63ec7eb95009bf89e") {
            return
        }

        client.workflows.searchRuns({
            document_id: object.id,
            search_term: "Score Grant Proposal"
        }).then((response) => {
            setReviewInProgress(response.runs.some(run => run.status === "RUNNING"))
        })
    }

    const handleReviewProgram = () => {
        if (!object?.properties?.easy_grant_id) {
            return
        }

        client.workflows.rules.execute(SCORE_GRANT_PROPOSAL_WORKFLOW_ID, [object.id], {})
            .then(() => {
                toast.success("Review process started", {
                    description: "The review process has been started successfully.",
                    duration: 2000
                })
                setReviewInProgress(true)
            })
    }

    const handleDownload = () => {
        if (!client || !object?.content?.source) {
            return
        }

        client.objects.getDownloadUrl(object.content.source)
            .then((response) => {
                const a = document.createElement("a")
                a.href = response.url
                a.download = object.content.name || object.name
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
            })
            .catch((error) => {
                toast.error("Failed to download file", {
                    description: error.message,
                    duration: 2000
                })
            })
    }

    const handlePanelStateChange = () => {
        const current = isPanelOpen
        localStorage.setItem(LAST_PANEL_STATE, current ? "closed" : "open")
        setIsPanelOpen(!current)
    }

    useEffect(() => {
        if (!client || isLoading || object) {
            return
        }

        searchDocument()
    }, [id])

    useEffect(() => {
        if (!client || isLoading || !object?.content || documentUrl) {
            return
        }

        client.objects.getDownloadUrl(object.content.source!).then((response) => {
            setDocumentUrl(response.url)
        })

        if (object?.properties?.easy_grant_id) {
            client.objects.search({ query: { type: "66c752ef3ec7eb95009bf8b0", match: { "properties.easy_grant_id": object?.properties.easy_grant_id }, all_revisions: false } }).
                then((response) => {
                    setReview(response.results[0] || undefined)
                })
        }

        if (object.type?.id === "66c752b63ec7eb95009bf89e") {
            checkReviewExecution()
        }

        retrieveMapping()
    }, [object])

    if (!object) {
        return <div></div>
    }

    const refresh = () => {
        searchDocument()
    }

    return (
        <div className='flex flex-col h-full'>
            <div className='w-full flex flex-row gap-2 items-center justify-between p-2 border-b'>
                <div className='text-2xl font-semibold truncate'>{object?.properties?.title || object?.name || object?.id}</div>
                <div className='flex flex-row gap-2'>
                    {
                        (object.type?.id === "66c752b63ec7eb95009bf89e" && !review) &&
                        <ActionTooltipButton icon={<SquareActivity />} variant="outline" tooltip="Review Grant Proposal" onClick={handleReviewProgram} disabled={reviewInProgress} />
                    }
                    {
                        removeFromCollection &&
                        <ActionTooltipButton icon={<FolderMinus />} variant="outline" tooltip="Remove from Project" onClick={() => removeFromCollection(object.id)} />
                    }
                    {
                        (mapping && object.properties) &&
                        <DownloadAsDocxButton content={object.properties} filename={object.name} mapping={JSON_MAPPINGS["681dd41e26456116c7f304ee"]} />
                    }
                    {
                        object?.content?.source &&
                        <ActionTooltipButton icon={<Download />} variant="outline" tooltip="Download" onClick={handleDownload} />
                    }
                    <ActionTooltipButton icon={<RotateCw />} variant="outline" tooltip="Refresh" onClick={refresh} />
                    {children}
                </div>
            </div>
            <div className="flex flex-row h-[calc(100vh-53px)]">
                <div className={`${isPanelOpen ? "w-[70%]" : "w-full"} h-full bg-gray-50 flex items-center justify-center`}>
                    {
                        documentUrl
                            ? <DocumentDisplay documentId={object.id} name={object.name} type={object.content.type!} url={documentUrl} mapping={mapping} />
                            : "No Document content Source"
                    }
                </div>
                <div className={`${isPanelOpen ? "w-[30%]" : ""} h-full bg-gray-100 border-l border-gray-300`}>
                    {
                        isPanelOpen &&
                        <div className='flex flex-col h-full'>
                            <div className='flex flex-row gap-1 items-center justify-between'>
                                <div className='flex flex-row gap-1 m-2 overflow-x-auto'>
                                    <Button
                                        onClick={() => setPanelState("properties")}
                                        variant="ghost"
                                        className={`${panelState === "properties" ? "bg-white" : ""} hover:opacity-60`}
                                    >
                                        Properties
                                    </Button>
                                    {
                                        (object.type?.id === "66c752b63ec7eb95009bf89e" && review) &&
                                        <Button
                                            onClick={() => setPanelState("score")}
                                            variant="ghost"
                                            className={`${panelState === "score" ? "bg-white" : ""} hover:opacity-60`}
                                        >
                                            Score
                                        </Button>
                                    }
                                    <Button
                                        onClick={() => setPanelState("similar")}
                                        variant="ghost"
                                        className={`${panelState === "similar" ? "bg-white" : ""} hover:opacity-60`}
                                    >
                                        Similar
                                    </Button>
                                    {
                                        project?.id === "67c5cbce25c319adea5d5c76" &&
                                        <Button
                                            onClick={() => setPanelState("chat")}
                                            variant="ghost"
                                            className={`${panelState === "chat" ? "bg-white" : ""} hover:opacity-60`}
                                        >
                                            Chat
                                        </Button>
                                    }
                                </div>
                                <Button variant='ghost' onClick={handlePanelStateChange} className='m-2 size-10'>
                                    <PanelRightClose />
                                </Button>
                            </div>
                            {
                                panelState === "properties" &&
                                <div className='flex-1 overflow-y-auto'>
                                    {
                                        object?.properties
                                            ? <PropertiesPanel value={object.properties} />
                                            : <NoPropertiesPanel />
                                    }
                                </div>
                            }
                            {
                                panelState === "score" &&
                                <div className='flex-1 overflow-y-auto'>
                                    {
                                        review?.properties
                                            ? <PropertiesPanel value={review.properties} />
                                            : <NoPropertiesPanel />
                                    }
                                </div>
                            }
                            {
                                panelState === "similar" &&
                                <div className='flex-1 overflow-y-auto'>
                                    {
                                        object
                                            ? <EmbeddingsPanel document={object} />
                                            : <NoEmbeddingsPanel />
                                    }
                                </div>
                            }
                        </div>
                    }
                    {
                        !isPanelOpen &&
                        <Button variant='ghost' onClick={handlePanelStateChange} className='m-2 size-10'>
                            <PanelRightOpen />
                        </Button>
                    }
                </div>
            </div>
        </div>
    )
}